import { useMemo } from 'react';
import type {
  RouteViewModel,
  RouteViewModelCorrection,
  RouteViewModelCorrectionOperation,
  RouteViewModelStop
} from '../../../types/routeViewModel';
import { useGetExecutionProgressQuery } from '../../../store/api/routesApi';
import { isLiveRouteStatus } from '../../mutationWorkspace';

function normalizeStatus(status: string | null | undefined): string {
  return String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
}

function inferStopBand(stop: RouteViewModelStop): 'COMPLETED' | 'CURRENT' | 'FUTURE' {
  const exec = stop.execution;
  if (exec?.isCompleted) return 'COMPLETED';
  const status = String(stop.status ?? exec?.executionState ?? '').toLowerCase();
  if (['loaded', 'unloaded', 'completed', 'done', 'executed', 'in_progress'].includes(status)) {
    return exec?.isCompleted ? 'COMPLETED' : 'CURRENT';
  }
  if (stop.actualAt) return 'COMPLETED';
  return 'FUTURE';
}

function buildFallbackCorrection(
  view: RouteViewModel,
  executionComplete: boolean
): RouteViewModelCorrection {
  const statusNorm = normalizeStatus(view.operation.status);
  const terminal = ['COMPLETED', 'CANCELLED'].includes(statusNorm);
  const inTransit = ['IN_TRANSIT', 'DELIVERED', 'WAITING_LOADING'].includes(statusNorm);
  const stops = view.structure?.stops ?? [];

  const stopBands = stops
    .filter((s) => s.id != null)
    .map((s) => {
      const band = inferStopBand(s);
      return {
        stopId: Number(s.id),
        band,
        editable: !terminal && band === 'FUTURE'
      };
    });

  let activeExecutionStopId: number | null = null;
  let activeExecutionStopUid: string | null = null;
  for (const s of stops) {
    const band = inferStopBand(s);
    if (band === 'CURRENT' && s.id != null) {
      activeExecutionStopId = Number(s.id);
      activeExecutionStopUid =
        (s.executionStopUid as string | undefined) ??
        (s as { executionStopUid?: string }).executionStopUid ??
        null;
      break;
    }
  }

  const allowedOperations: RouteViewModelCorrectionOperation[] = [];
  const advisories: RouteViewModelCorrection['advisories'] = [];

  if (!terminal) {
    if (inTransit || isLiveRouteStatus(view.operation.status)) {
      allowedOperations.push('RESOURCE_DRIVER', 'RESOURCE_VEHICLE', 'RESOURCE_TRAILER');
      advisories.push({
        code: 'PENALTY_AT_COMPLETION',
        message: 'При наличии нарушения штраф выставляется при завершении маршрута',
        severity: 'info'
      });
    }
    if (stopBands.some((b) => b.editable)) {
      allowedOperations.push('STOP_FUTURE_EDIT');
    }
    allowedOperations.push('ADD_CARGO_SEGMENT', 'ADD_UNLOAD', 'EARLY_COMPLETION');
    if (executionComplete) {
      allowedOperations.push('CARRIER_REPLACEMENT');
    }
  }

  advisories.push({
    code: 'CARRIER_REQUIRES_NEW_ROUTE',
    message: 'Смена перевозчика выполняется через завершение текущего маршрута и создание нового',
    severity: 'info'
  });

  const routeUpdatedAt =
    view.correction?.routeUpdatedAt ??
    view.sourceRevision?.routeUpdatedAt ??
    view.builtAt ??
    new Date().toISOString();

  return {
    routeUpdatedAt,
    isCompletionLocked: terminal,
    isTerminalRoute: terminal,
    executionLock: {
      inTransit,
      activeExecutionStopId,
      activeExecutionStopUid,
      executionComplete
    },
    stopBands,
    allowedOperations,
    advisories
  };
}

export type CorrectionContext = {
  correction: RouteViewModelCorrection;
  routeUpdatedAt: string;
  isOperationAllowed: (op: RouteViewModelCorrectionOperation) => boolean;
  getStopBand: (stopId: number) => RouteViewModelCorrection['stopBands'][number] | undefined;
  completedStopIds: number[];
  currentStopIds: number[];
  futureEditableStopIds: number[];
  fromBackendSlice: boolean;
};

export function useCorrectionContext(routeId: number, view: RouteViewModel): CorrectionContext {
  const hasBackendSlice = view.correction != null;
  const { data: executionProgress } = useGetExecutionProgressQuery(routeId, {
    skip: hasBackendSlice
  });

  return useMemo(() => {
    const executionComplete =
      view.correction?.executionLock.executionComplete ??
      view.executionDashboard?.executionComplete ??
      executionProgress?.progress?.executionComplete ??
      false;

    const correction = view.correction ?? buildFallbackCorrection(view, executionComplete);

    const bandByStopId = new Map(correction.stopBands.map((b) => [b.stopId, b]));

    return {
      correction,
      routeUpdatedAt: correction.routeUpdatedAt,
      isOperationAllowed: (op) => correction.allowedOperations.includes(op),
      getStopBand: (stopId) => bandByStopId.get(stopId),
      completedStopIds: correction.stopBands.filter((b) => b.band === 'COMPLETED').map((b) => b.stopId),
      currentStopIds: correction.stopBands.filter((b) => b.band === 'CURRENT').map((b) => b.stopId),
      futureEditableStopIds: correction.stopBands.filter((b) => b.editable).map((b) => b.stopId),
      fromBackendSlice: hasBackendSlice
    };
  }, [view, executionProgress, hasBackendSlice]);
}
